import { z } from 'zod';
import { compareDecimals } from '@/shared/decimal';

const DecimalLikeSchema = z.union([z.string(), z.number()]).transform((value) =>
  typeof value === 'number' ? String(value) : value,
);

const NullableDecimalLikeSchema = z.union([DecimalLikeSchema, z.null()]).transform((value) => value ?? null);

// Accepts a bare decimal string OR a finite number, and normalizes both to a
// string. Garbage like "", "NaN", "not-a-number" are explicitly rejected so the
// validated union still surfaces parse errors for malformed payloads.
const ValidatedDecimalStringSchema = z
  .union([
    z.string().regex(/^-?\d+(\.\d+)?$/),
    z.number().finite(),
  ])
  .transform((v) => (typeof v === 'number' ? v.toString() : v));

const ScenarioPnlObjectSchema = z.object({
  trade_pnl: NullableDecimalLikeSchema.optional(),
  book_pnl_before: NullableDecimalLikeSchema.optional(),
  book_pnl_after: NullableDecimalLikeSchema.optional(),
  delta: NullableDecimalLikeSchema.optional(),
  worst: z.boolean().optional(),
});

// Accepts either the legacy bare-decimal-string shape (emitted by the local
// backend) OR the rich object shape (v2 backend).  Both branches normalize to
// the same output so the component never has to branch on the wire format.
const ScenarioPnlValueSchema = z
  .union([ValidatedDecimalStringSchema, ScenarioPnlObjectSchema])
  .transform((value) => {
    if (typeof value === 'string') {
      return {
        trade_pnl: null as string | null,
        book_pnl_before: null as string | null,
        book_pnl_after: null as string | null,
        delta: value,
        worst: false,
      };
    }
    return {
      trade_pnl: value.trade_pnl ?? null,
      book_pnl_before: value.book_pnl_before ?? null,
      book_pnl_after: value.book_pnl_after ?? null,
      delta: value.delta ?? null,
      worst: value.worst ?? false,
    };
  });

// var_contribution: accepts either legacy bare decimal OR rich {before,after,delta}.
// Both normalize to {before, after, delta} so the component is shape-agnostic.
const LegacyVarContributionSchema = ValidatedDecimalStringSchema.transform((v) => ({
  before: null as string | null,
  after: null as string | null,
  delta: v,
}));
const RichVarContributionSchema = z.object({
  before: NullableDecimalLikeSchema.optional(),
  after: NullableDecimalLikeSchema.optional(),
  delta: NullableDecimalLikeSchema.optional(),
});
const BeforeAfterDeltaVarSchema = z
  .union([LegacyVarContributionSchema, RichVarContributionSchema])
  .transform((v) => ({
    before: v.before ?? null,
    after: v.after ?? null,
    delta: v.delta ?? null,
  }))
  .nullable()
  .optional()
  .transform((value) => value ?? null);

const BeforeAfterDeltaSchema = z
  .object({
    before: NullableDecimalLikeSchema.optional(),
    after: NullableDecimalLikeSchema.optional(),
    delta: NullableDecimalLikeSchema.optional(),
  })
  .nullable()
  .optional()
  .transform((value) => value ?? null);

const StringRecordSchema = z.record(z.string(), z.string());

export const BondPretradeInstrumentPayloadSchema = z.object({
  id: z.string().optional(),
  instrument_type: z.string().optional(),
  name: z.string().optional(),
  currency: z.string().optional(),
  maturity_date: z.string().nullable().optional(),
  identifiers: StringRecordSchema.optional(),
  details: z.record(z.string(), z.unknown()).optional(),
});

export const BondPretradeTicketPayloadSchema = z.object({
  instrument_identifier: z.string().optional(),
  instrument: BondPretradeInstrumentPayloadSchema.optional(),
  side: z.enum(['BUY', 'SELL']).optional(),
  notional: z.string().optional(),
  price: z.string().optional(),
  counterparty_id: z.string().optional(),
  book_id: z.string().optional(),
  strategy: z.string().optional(),
  trade_date: z.string().optional(),
  settle_date: z.string().optional(),
});

const DecimalStringSchema = z
  .string()
  .trim()
  .regex(/^-?\d+(\.\d+)?$/, 'Must be a decimal value');

export const BondPretradeTicketFormSchema = z
  .object({
    instrument_identifier: z.string().trim().min(1, 'Instrument is required'),
    side: z.enum(['BUY', 'SELL']),
    notional: DecimalStringSchema.refine((value) => {
      const comparison = compareDecimals(value, '0');
      return comparison !== null && comparison > 0;
    }, 'Notional must be greater than zero'),
    price: z.union([z.literal(''), DecimalStringSchema]),
    counterparty_id: z.string().trim().min(1, 'Counterparty is required'),
    book_id: z.string().trim().min(1, 'Book is required'),
    strategy: z.string().trim().optional(),
    trade_date: z.string().trim().min(1, 'Trade date is required'),
    settle_date: z.string().trim().min(1, 'Settlement date is required'),
  })
  .superRefine((value, ctx) => {
    if (value.trade_date.length > 0 && value.settle_date.length > 0 && value.settle_date < value.trade_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Settlement date must be on or after trade date',
        path: ['settle_date'],
      });
    }
  });

export const BondPretradePriceResponseSchema = z.object({
  as_of: z.string(),
  valuation_date: z.string(),
  price: z.object({
    clean: DecimalLikeSchema,
    dirty: DecimalLikeSchema,
    accrued: DecimalLikeSchema,
  }),
  yield: z
    .object({
      ytm: NullableDecimalLikeSchema.optional(),
      ytw: NullableDecimalLikeSchema.optional(),
      ytc: NullableDecimalLikeSchema.optional(),
      ytp: NullableDecimalLikeSchema.optional(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  spread: z
    .object({
      z_spread_bps: NullableDecimalLikeSchema.optional(),
      asw_bps: NullableDecimalLikeSchema.optional(),
      g_spread_bps: NullableDecimalLikeSchema.optional(),
      i_spread_bps: NullableDecimalLikeSchema.optional(),
      oas_bps: NullableDecimalLikeSchema.optional(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  risk: z
    .object({
      modified_duration: NullableDecimalLikeSchema.optional(),
      macaulay_duration: NullableDecimalLikeSchema.optional(),
      effective_duration: NullableDecimalLikeSchema.optional(),
      convexity: NullableDecimalLikeSchema.optional(),
      dv01: NullableDecimalLikeSchema.optional(),
      key_rate_dv01: z
        .array(
          z.object({
            tenor: z.string(),
            dv01_value: DecimalLikeSchema,
          }),
        )
        .optional()
        .default([]),
      spread_duration: NullableDecimalLikeSchema.optional(),
      cs01: NullableDecimalLikeSchema.optional(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  cashflow_schedule: z
    .array(
      z.object({
        payment_date: z.string(),
        cashflow_type: z.string(),
        amount: DecimalLikeSchema,
        discount_factor: DecimalLikeSchema,
        present_value: DecimalLikeSchema,
        days_to_payment: z.number(),
        dcf: DecimalLikeSchema,
      }),
    )
    .default([]),
});

export const BondPretradeCheckResponseSchema = z.object({
  valuation_date: z.string(),
  limits: z
    .array(
      z.object({
        name: z.string(),
        current: NullableDecimalLikeSchema.optional(),
        after: NullableDecimalLikeSchema.optional(),
        limit: NullableDecimalLikeSchema.optional(),
        utilization_pct: NullableDecimalLikeSchema.optional(),
        warning_threshold_pct: NullableDecimalLikeSchema.optional(),
        breach: z.boolean(),
        binding: z.boolean(),
      }),
    )
    .default([]),
  credit: z
    .object({
      counterparty_name: z.string().nullable().optional(),
      counterparty_rating: z
        .object({
          sp: z.string().nullable().optional(),
          moodys: z.string().nullable().optional(),
          fitch: z.string().nullable().optional(),
          watch: z.string().nullable().optional(),
          outlook: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      cds5y_bps: NullableDecimalLikeSchema.optional(),
      probability_of_default: NullableDecimalLikeSchema.optional(),
      cva_estimate: NullableDecimalLikeSchema.optional(),
      exposure_before: NullableDecimalLikeSchema.optional(),
      exposure_after: NullableDecimalLikeSchema.optional(),
      issuer: z
        .object({
          name: z.string().nullable().optional(),
          sector: z.string().nullable().optional(),
          country: z.string().nullable().optional(),
          rating: z
            .object({
              sp: z.string().nullable().optional(),
              moodys: z.string().nullable().optional(),
              fitch: z.string().nullable().optional(),
            })
            .nullable()
            .optional(),
          outlook: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  portfolio_impact: z
    .object({
      scope: z.string(),
      dv01: BeforeAfterDeltaSchema,
      modified_duration: BeforeAfterDeltaSchema,
      convexity: BeforeAfterDeltaSchema,
      notional: BeforeAfterDeltaSchema,
      cash: BeforeAfterDeltaSchema,
      key_rate_dv01_delta: z
        .array(
          z.object({
            tenor: z.string(),
            value: DecimalLikeSchema,
            // F5 portfolio view carries before/after per bucket; the risk tab
            // only needs `value` (delta), so both extra fields stay optional.
            before: NullableDecimalLikeSchema.optional(),
            after: NullableDecimalLikeSchema.optional(),
          }),
        )
        .optional()
        .default([]),
      scenario_pnl: z.record(z.string(), ScenarioPnlValueSchema).optional().default({}),
      var_contribution: BeforeAfterDeltaVarSchema,
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  relative_value: z
    .object({
      spread_vs_issuer_curve_bps: NullableDecimalLikeSchema.optional(),
      z_score_3m: NullableDecimalLikeSchema.optional(),
      rich_or_cheap: z.string().nullable().optional(),
    })
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  cash_impact: z.object({
    settle_date: z.string(),
    amount: DecimalLikeSchema,
    currency: z.string(),
  }),
  scope_truncated: z.boolean(),
});

export const InstrumentComparablesResponseSchema = z.object({
  reference: z.object({
    id: z.string(),
    issuer: z.string().nullable().optional().transform((value) => value ?? null),
    sector: z.string().nullable().optional().transform((value) => value ?? null),
    rating: z.string().nullable().optional().transform((value) => value ?? null),
    tenor_years: NullableDecimalLikeSchema.optional(),
  }),
  peers: z
    .array(
      z.object({
        id: z.string(),
        isin: z.string().nullable().optional().transform((value) => value ?? null),
        issuer: z.string().nullable().optional().transform((value) => value ?? null),
        sector: z.string().nullable().optional().transform((value) => value ?? null),
        rating: z.string().nullable().optional().transform((value) => value ?? null),
        tenor_years: NullableDecimalLikeSchema.optional(),
        z_spread_bps: NullableDecimalLikeSchema.optional(),
        g_spread_bps: NullableDecimalLikeSchema.optional(),
        yield: NullableDecimalLikeSchema.optional(),
        liquidity_score: NullableDecimalLikeSchema.optional(),
        proxy: z.boolean().nullable().optional().transform((value) => value ?? null),
        match_basis: z.array(z.string()).optional().default([]),
      }),
    )
    .default([]),
});

export const InstrumentSpreadHistoryResponseSchema = z.object({
  instrument_id: z.string(),
  window: z.enum(['1m', '3m', '6m', '1y']),
  points: z
    .array(
      z.object({
        date: z.string(),
        z_spread_bps: NullableDecimalLikeSchema.optional(),
        g_spread_bps: NullableDecimalLikeSchema.optional(),
      }),
    )
    .default([]),
  stats: z.object({
    z_spread_mean: NullableDecimalLikeSchema.optional(),
    z_spread_stdev: NullableDecimalLikeSchema.optional(),
    z_spread_latest: NullableDecimalLikeSchema.optional(),
    z_score: NullableDecimalLikeSchema.optional(),
  }),
});

export type BondPretradeInstrumentPayload = z.infer<typeof BondPretradeInstrumentPayloadSchema>;
export type BondPretradeTicketPayload = z.infer<typeof BondPretradeTicketPayloadSchema>;
export type BondPretradeFormValues = z.infer<typeof BondPretradeTicketFormSchema>;
export type BondPretradePriceResponse = z.infer<typeof BondPretradePriceResponseSchema>;
export type BondPretradeCheckResponse = z.infer<typeof BondPretradeCheckResponseSchema>;
export type BondPretradeScenarioPnlValue = z.infer<typeof ScenarioPnlValueSchema>;
export type InstrumentComparablesResponse = z.infer<typeof InstrumentComparablesResponseSchema>;
export type InstrumentSpreadHistoryResponse = z.infer<typeof InstrumentSpreadHistoryResponseSchema>;
